import React from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { FONT_SIZES } from '../../Theme';
import getCoolScrollbarSx from '../ui/scrollbarSx';

const DRILLDOWN_HEADINGS = Object.freeze({
  status: 'Status',
  type: 'Type',
  go: 'Go to page',
});

function SectionHeading({ children }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 2,
        pt: 0.5,
        pb: 1,
      }}
    >
      <Typography
        component="h3"
        sx={{
          fontSize: FONT_SIZES.body,
          fontWeight: 700,
          color: 'text.primary',
          letterSpacing: '-0.01em',
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function SuggestionPrimary({ item }) {
  return (
    <Typography
      component="span"
      sx={{
        fontSize: FONT_SIZES.bodySm,
        fontWeight: 600,
        color: 'text.primary',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {item.label}
    </Typography>
  );
}

/**
 * Dropdown panel for dashboard omnibar.
 * Empty: Filter by categories. Drill-down / typed: completions, pages, entities, marketplace.
 */
function DashboardOmnibarSuggestions({
  mode,
  items,
  onSelect,
  listId,
  activeIndex = -1,
  optionIdPrefix = 'dashboard-omnibar-option',
  categoryMode = null,
}) {
  if (!items.length) return null;

  const isEmptyMode = mode === 'empty';
  const isDrilldownMode = mode === 'drilldown';
  const firstBrowseIndex = items.findIndex((item) => item.kind === 'marketplace-browse');
  const showResultsHeading = !isEmptyMode && !isDrilldownMode && items.some((item) => (
    item.kind === 'destination' || item.kind === 'entity' || item.kind === 'marketplace'
  ));
  const firstTypedResultIndex = items.findIndex((item) => (
    item.kind === 'destination' || item.kind === 'entity' || item.kind === 'marketplace'
  ));
  const drilldownHeading = categoryMode
    ? (DRILLDOWN_HEADINGS[categoryMode] || 'Filter by')
    : 'Filter by';

  return (
    <Box
      role="listbox"
      id={listId}
      data-testid="dashboard-omnibar-suggestions"
      sx={(theme) => ({
        width: '100%',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2.5,
        boxShadow: theme.palette.chat?.windowShadow || theme.shadows[3],
        py: 1,
        maxHeight: 'min(70vh, 420px)',
        overflowX: 'hidden',
        overflowY: 'auto',
        ...getCoolScrollbarSx(theme),
      })}
    >
      {isEmptyMode ? (
        <SectionHeading>Filter by</SectionHeading>
      ) : null}
      {isDrilldownMode ? (
        <SectionHeading>{drilldownHeading}</SectionHeading>
      ) : null}
      {showResultsHeading && firstTypedResultIndex === 0 ? (
        <SectionHeading>Results</SectionHeading>
      ) : null}
      <List disablePadding dense>
        {items.map((item, index) => {
          const showResultsBefore = (
            !isEmptyMode
            && !isDrilldownMode
            && showResultsHeading
            && index === firstTypedResultIndex
            && firstTypedResultIndex > 0
          );
          const showBrowseDivider = (
            isEmptyMode
            && item.kind === 'marketplace-browse'
            && firstBrowseIndex === index
            && index > 0
          );

          return (
            <React.Fragment key={item.id}>
              {showResultsBefore ? (
                <SectionHeading>Results</SectionHeading>
              ) : null}
              {showBrowseDivider ? (
                <Box
                  aria-hidden
                  sx={{
                    borderTop: 1,
                    borderColor: 'divider',
                    my: 0.5,
                  }}
                />
              ) : null}
              <ListItemButton
                id={`${optionIdPrefix}-${index}`}
                role="option"
                aria-selected={activeIndex === index}
                selected={activeIndex === index}
                disableRipple
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => onSelect(item)}
                data-testid={`dashboard-omnibar-option-${item.kind}`}
                sx={{
                  px: 2,
                  py: 1,
                  alignItems: 'flex-start',
                  gap: 1,
                  bgcolor: 'transparent',
                  '&:hover, &:active, &:focus, &.Mui-focusVisible': {
                    bgcolor: 'background.softHover',
                  },
                  '&.Mui-selected': {
                    bgcolor: 'background.soft',
                  },
                  '&.Mui-selected:hover, &.Mui-selected:focus, &.Mui-selected.Mui-focusVisible': {
                    bgcolor: 'background.softHover',
                  },
                }}
              >
                <ListItemText
                  primary={<SuggestionPrimary item={item} />}
                  secondary={item.description || null}
                  secondaryTypographyProps={{
                    sx: {
                      fontSize: FONT_SIZES.caption,
                      color: 'text.secondary',
                      mt: 0.25,
                    },
                  }}
                  sx={{ my: 0, minWidth: 0 }}
                />
              </ListItemButton>
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
}

export default DashboardOmnibarSuggestions;
